import React, { useState, useEffect } from 'react';
import { Download, FileSpreadsheet, Package, Truck, AlertTriangle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../../../context/MyContext';
import NavBar from '../../../component/b2c/navBar/navBar';
import Sidebar from '../../../component/b2c/sidebar/sidebar';
import { Base_Url, forwardorderlistall } from '../../../config/config';
import { TbReportSearch } from "react-icons/tb";
import '../../../css/custom-scrollbar.css'
import { toast } from 'react-toastify';
import Papa from "papaparse";
import FullPageLoader from '../../../../src/component/fullPageLoader/fullPageLoader'

const REPORT_STYLES = {
  'MIS Report': { icon: FileSpreadsheet, accent: 'from-violet-600 to-purple-600', glow: 'shadow-[0_0_15px_rgba(139,92,246,0.3)]' },
  'Order Product Report': { icon: Package, accent: 'from-sky-600 to-blue-600', glow: 'shadow-[0_0_15px_rgba(14,165,233,0.3)]' },
  'Current Shipment Report': { icon: Truck, accent: 'from-emerald-600 to-green-600', glow: 'shadow-[0_0_15px_rgba(16,185,129,0.3)]' },
  'NDR Report': { icon: AlertTriangle, accent: 'from-rose-600 to-red-600', glow: 'shadow-[0_0_15px_rgba(244,63,94,0.3)]' },
};
// ⚡ Cache helpers for instant load
const SHIPMENT_CACHE_PREFIX = 'b2c_all_shipments_cache_';
const readShipmentCache = (cusId) => {
  try {
    const raw = localStorage.getItem(SHIPMENT_CACHE_PREFIX + (cusId || ''));
    const parsed = raw ? JSON.parse(raw) : null;
    if (Array.isArray(parsed)) return parsed;
  } catch (e) { /* ignore */ }
  return [];
};
const writeShipmentCache = (cusId, data) => {
  try { localStorage.setItem(SHIPMENT_CACHE_PREFIX + (cusId || ''), JSON.stringify(data)); } catch (e) { }
};

const ReportsDropdown = () => {
  const { isDarkMode, isToggleSidebar,setIsToggleSidebar } = useMyContext();
  const navigate = useNavigate();
  const [isPageLoading, isSetPageLoading] = useState();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRanges, setDateRanges] = useState({
    'MIS Report': { startDate: '', endDate: '' },
    'Order Product Report': { startDate: '', endDate: '' },
    'Current Shipment Report': { startDate: '', endDate: '' },
    'NDR Report': { startDate: '', endDate: '' }
  });

  const validateDate = (date) => {
    const selectedDate = new Date(date);
    const today = new Date();
    return selectedDate <= today;
  };

  const reports = [
    {
      name: 'MIS Report',
      description: 'Management information system report with detailed order',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'AWB Number': 'awb_number',
        'Pickup Name': 'pickup_name',
        'Pickup Address': 'pickup_address',
        'Pickup City': 'pickup_city',
        'Pickup Pin Code': 'pickup_pincode',
        'Pickup Country': 'pickup_country',
        'Pickup Phone': 'pickup_phone',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Phone': 'phone',
        'Email': 'email',
        'Payment Mode': 'payment_mode',
        'Total Amount': 'total_amount',
        'Created At': 'created_at',
        'Partner': 'partner',
        'Warehouse ID': 'warehouse_id',
        'Shipment Type': 'shipment_type',
        'Payment ID': 'payment_id',
        'Product Description': 'product_desc',
        'COD Amount': 'cod_amount',
        'Quantity': 'quantity',
        'Width': 'width',
        'Height': 'height',
        'Length': 'length',
        'Weight': 'weight',
        'Shipping Mode': 'shipping_mode',
        'Address Type': 'address_type',
        'Request ID': 'request_id',
        'Is NDR': 'is_ndr',
        'Status': 'status',
        'Debit Amount': 'debit_amount'
      }
    },
    {
      name: 'Order Product Report',
      description: 'Detailed product information for all orders',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Product Description': 'product_desc',
        'Quantity': 'quantity',
        'SKU': 'sku',
        'AWB Number': 'awb_number',
        'Created At': 'created_at'
      }
    },
    {
      name: 'Current Shipment Report',
      description: 'Real-time status of all active shipments',
      columnMapping: {
        'Order ID': 'order_id',
        'User ID': 'user_id',
        'First Name': 'first_name',
        'Last Name': 'last_name',
        'Product Description': 'product_desc',
        'Quantity': 'quantity',
        'SKU': 'sku',
        'AWB Number': 'awb_number',
        'Drop Address': 'drop_address',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Drop Pin': 'drop_pin',
        'Status': 'status'
      }
    },
    {
      name: 'NDR Report',
      description: 'Non-delivery report with attempt details and reasons',
      columnMapping: {
        'Order ID': 'order_id',
        'First Name': 'first_name',
        'User ID': 'user_id',
        'Last Name': 'last_name',
        'Phone': 'phone',
        'Product Description': 'product_desc',
        'AWB Number': 'awb_number',
        'Drop Address': 'drop_address',
        'Drop Pin': 'drop_pin',
        'Drop City': 'drop_city',
        'Drop State': 'drop_state',
        'Status': 'status',
        'NDR Attempt': 'ndr_attempt',
        'Updated At': 'updated_at',
        'NDR Reason': 'ndr_reason'
      }
    }
  ];

  useEffect(() => {
    fetchOrderData();
  }, []);

  const fetchOrderData = async () => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');

    // ⚡ Instant load from cache
    const cachedData = readShipmentCache(cusid);
    if (cachedData.length > 0) {
      setData(cachedData);
    } else {
      isSetPageLoading(true); // Full screen loader ONLY if cache is empty
    }

    try {
      const response = await fetch(Base_Url + forwardorderlistall, {
        method: 'GET',
        headers: {
          token,
          cus_id: cusid,
          'Content-Type': 'application/json',
        },
      });
      const result = await response.json();

      if (response.ok) {
        const rawData = result.data || [];
        writeShipmentCache(cusid, rawData); // update cache for next time/other pages
        setData(rawData);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Failed to fetch data. Please try again later.');
    } finally {
      setLoading(false);
      isSetPageLoading(false);
    }
  };

  const handleDateChange = (reportName, field, value) => {
    if (value && !validateDate(value)) {
      toast.error('Future dates are not allowed');
      return;
    }

    if (field === 'endDate' && value) {
      const startDate = dateRanges[reportName].startDate;
      if (startDate && new Date(value) < new Date(startDate)) {
        toast.error('End date cannot be before start date');
        return;
      }
    }

    if (field === 'startDate' && value) {
      const endDate = dateRanges[reportName].endDate;
      if (endDate && new Date(value) > new Date(endDate)) {
        toast.error('Start date cannot be after end date');
        return;
      }
    }

    setDateRanges(prev => ({
      ...prev,
      [reportName]: {
        ...prev[reportName],
        [field]: value
      }
    }));
  };

  const isDownloadDisabled = (reportName) => {
    const { startDate, endDate } = dateRanges[reportName];
    return loading || !startDate || !endDate;
  };

  const downloadCSV = async (report) => {
    const { startDate, endDate } = dateRanges[report.name];

    let filteredData = data;
    if (startDate && endDate) {
      filteredData = data.filter(item => {
        const itemDate = new Date(item.created_at);
        const start = new Date(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        return itemDate >= start && itemDate <= end;
      });
    }

    if (filteredData.length === 0) {
      toast.error('No data found for the selected date range');
      return;
    }

    const csvData = filteredData.map(item => {
      const row = {};
      Object.entries(report.columnMapping).forEach(([displayName, apiKey]) => {
        row[displayName] = item[apiKey] !== undefined && item[apiKey] !== null ? item[apiKey] : 'N/A';
      });
      return row;
    });

    try {
      const csv = Papa.unparse(csvData, {
        header: true,
        quotes: true,
        quoteChar: '"',
        escapeChar: '"'
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.name}_${startDate || 'all'}_to_${endDate || 'all'}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`${report.name} downloaded successfully!`);
    } catch (error) {
      console.error('Error generating CSV:', error);
      toast.error('Failed to generate CSV file');
    }
  };
  
  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${
      isDarkMode ? 'dark bg-[#0B1120]' : 'bg-gray-50'
    }`}>
      {/* Premium ambient glows */}
      {isDarkMode ? (
        <>
          <div className="pointer-events-none absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-indigo-600/15 blur-[120px]" />
          <div className="pointer-events-none absolute bottom-0 -right-32 h-[400px] w-[400px] rounded-full bg-purple-600/10 blur-[100px]" />
        </>
      ) : (
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 blur-3xl" />
      )}

      <NavBar />
      <div className="flex flex-1 overflow-hidden relative z-10">
               {isToggleSidebar && (
          <div
            className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm md:hidden"
            onClick={() => setIsToggleSidebar(false)}
          />
        )}

        <div
          className={`
                     fixed inset-y-0 left-0 z-50 w-64
                     ${isToggleSidebar ? "translate-x-0" : "-translate-x-full"}
                     md:static md:inset-auto md:z-auto md:translate-x-0
                     ${isToggleSidebar ? "md:w-64" : "md:w-[72px]"}
                     m-3
                     rounded-3xl
                 
                     overflow-hidden
                     transition-all
                     duration-300
                     ease-in-out
                   `}
        >
          <Sidebar />
        </div>
        <FullPageLoader isLoading={isPageLoading} message="Processing your request..." />

        <div className={`flex-1 p-3 md:p-4 w-full overflow-y-auto custom-scrollbar transition-colors duration-300 ${
          isDarkMode ? 'bg-[#0B1120]' : 'bg-gray-200'
        }`}>
          <div className="max-w-6xl mx-auto">

            {/* Premium Header */}
            <div className="flex-shrink-0 mb-4">
              <div className="flex items-center gap-3">
                <div className={`p-2.5 rounded-xl border transition-all duration-300 ${
                  isDarkMode
                    ? 'bg-gradient-to-br from-rose-500/20 to-red-500/10 border-rose-500/30 shadow-[0_0_20px_rgba(244,63,94,0.2)]'
                    : 'border-gray-200 bg-white'
                }`}>
                  <TbReportSearch size={18} className={isDarkMode ? 'text-rose-400' : 'text-red-500'} />
                </div>
                <div>
                  <h1 className={`text-base font-bold tracking-tight transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-800'
                  }`}>Reports Dashboard</h1>
                  <p className={`text-[10px] transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-500' : 'text-gray-400'
                  }`}>Select date range and export reports</p>
                </div>
              </div>
            </div>

            {error && (
              <div className={`mb-4 p-3 rounded-xl border flex items-center gap-2 ${
                isDarkMode
                  ? 'border-rose-500/40 bg-rose-500/10'
                  : 'border-red-200 bg-red-50'
              }`}>
                <AlertTriangle size={14} className={isDarkMode ? 'text-rose-400' : 'text-red-500'} />
                <p className={`text-[11px] font-semibold ${
                  isDarkMode ? 'text-rose-300' : 'text-red-600'
                }`}>{error}</p>
              </div>
            )}

            {/* Reports Grid */}
            <div className="grid gap-3 md:grid-cols-2">
              {reports.map((report, idx) => {
                const style = REPORT_STYLES[report.name];
                const Icon = style.icon;
                const disabled = isDownloadDisabled(report.name);

                return (
                  <div
                    key={report.name}
                    className={`rounded-2xl border p-4 transition-all duration-300 ${
                      isDarkMode
                        ? 'bg-gradient-to-br from-[#111827] to-[#0F172A] border-gray-800 hover:border-indigo-500/40 hover:shadow-[0_0_25px_rgba(99,102,241,0.15)]'
                        : 'bg-white border-gray-200 shadow-sm hover:shadow-md'
                    }`}
                  >
                    {/* Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`p-2.5 rounded-xl bg-gradient-to-br ${style.accent} ${isDarkMode ? style.glow : ''}`}>
                        <Icon size={15} className="text-white" />
                      </div>
                      <div>
                        <h2 className={`text-[11px] font-bold uppercase tracking-wider transition-colors duration-300 ${
                          isDarkMode ? 'text-white' : 'text-gray-800'
                        }`}>
                          {report.name}
                        </h2>
                        <p className={`text-[10px] transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>{report.description}</p>
                        <p className={`text-[9px] mt-0.5 font-mono ${
                          isDarkMode ? 'text-indigo-300' : 'text-gray-400'
                        }`}>
                          {Object.keys(report.columnMapping).length} fields
                        </p>
                      </div>
                    </div>

                    {/* Date Inputs */}
                    <div className="grid grid-cols-2 gap-2 mb-3">
                      <div>
                        <label className={`block text-[9px] font-bold uppercase tracking-wider mb-1 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          Start Date
                        </label>
                        <div className="relative">
                          <Calendar size={12} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${
                            isDarkMode ? 'text-indigo-400' : 'text-gray-400'
                          }`} />
                          <input
                            type="date"
                            value={dateRanges[report.name].startDate}
                            onChange={(e) => handleDateChange(report.name, 'startDate', e.target.value)}
                            className={`w-full pl-7 pr-2 py-1.5 text-[10px] rounded-lg border focus:outline-none transition-all duration-300 ${
                              isDarkMode
                                ? 'bg-white/[0.03] border-white/10 text-white focus:border-indigo-500/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-indigo-500/20'
                                : 'bg-white border-gray-200 text-gray-700 focus:ring-1 focus:ring-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                      <div>
                        <label className={`block text-[9px] font-bold uppercase tracking-wider mb-1 ${
                          isDarkMode ? 'text-gray-500' : 'text-gray-500'
                        }`}>
                          End Date
                        </label>
                        <div className="relative">
                          <Calendar size={12} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${
                            isDarkMode ? 'text-rose-400' : 'text-gray-400'
                          }`} />
                          <input
                            type="date"
                            value={dateRanges[report.name].endDate}
                            onChange={(e) => handleDateChange(report.name, 'endDate', e.target.value)}
                            className={`w-full pl-7 pr-2 py-1.5 text-[10px] rounded-lg border focus:outline-none transition-all duration-300 ${
                              isDarkMode
                                ? 'bg-white/[0.03] border-white/10 text-white focus:border-indigo-500/60 focus:bg-white/[0.06] focus:ring-2 focus:ring-indigo-500/20'
                                : 'bg-white border-gray-200 text-gray-700 focus:ring-1 focus:ring-gray-400'
                            }`}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Download Button */}
                    <button
                      onClick={() => downloadCSV(report)}
                      disabled={disabled}
                      className={`group w-full py-2 text-[10px] font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-1.5 ${
                        disabled
                          ? isDarkMode
                            ? 'bg-gray-800/40 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : isDarkMode
                            ? `bg-gradient-to-r ${style.accent} text-white hover:shadow-[0_0_25px_rgba(99,102,241,0.5)] hover:scale-[1.02]`
                            : 'bg-red-600 text-white hover:opacity-85'
                      }`}
                    >
                      <Download size={12} />
                      Download CSV
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsDropdown;